import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  Divider,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputLeftAddon,
  Tag,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import { getArticle } from "api/requests/getArticle";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";

import useAuth from "hooks/useAuth";

const modules = {
  toolbar: [
    [{ header: [] }],
    [{ font: [] }],
    ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
    [{ color: [] }, { background: [] }],
    [
      { list: "ordered" },
      { list: "bullet" },
      { align: [] },
      { indent: "+1" },
      { indent: "-1" },
    ],
    ["link", "image"],
    ["clean"],
  ],
};

const formats = [
  "header",
  "font",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "code-block",
  "color",
  "background",
  "list",
  "bullet",
  "align",
  "indent",
  "link",
  "image",
];

function Article() {
  const { articleId } = useParams();
  const [article, setArticle] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [currentTitle, setCurrentTitle] = useState("");
  const [currentDescription, setCurrentDescription] = useState("");
  const [currentSummary, setCurrentSummary] = useState("");
  const [currentTags, setCurrentTags] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [images, setImages] = useState([]);
  const dividerColor = useColorModeValue("#dcdcdc", "#4a5568");
  const cardBgColor = useColorModeValue("white", "#2d3748");
  const headingColor = useColorModeValue("gray.700", "white");
  const textColor = useColorModeValue("gray.700", "gray.300");
  const buttonBgColor = useColorModeValue("blue.500", "blue.300");

  const { hasRequiredRole } = useAuth();

  const toast = useToast();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await getArticle(articleId);
        const fetchedArticle = response.data;
        setArticle(fetchedArticle);
        setCurrentTitle(fetchedArticle.title);
        setCurrentDescription(fetchedArticle.description);
        setCurrentSummary(fetchedArticle.summary);
        setCurrentTags(fetchedArticle.tags);
        extractImages(fetchedArticle.description);
      } catch (err) {
        console.error("Error fetching article:", err);
      }
    };

    fetchArticle();
  }, [articleId]);

  const extractImages = (description) => {
    const imgTags = description.match(/<img [^>]*src="[^"]*"[^>]*>/gm);
    const imgSrcs = imgTags
      ? imgTags.map((img) => img.match(/src="([^"]*)"/)[1])
      : [];
    setImages(imgSrcs);
  };

  if (!article) {
    return <Text color={textColor}>Loading...</Text>;
  }

  const handleEditClick = () => {
    if (isEditing) {
      handleUpdate();
    }
    setIsEditing(!isEditing);
  };

  const handleTagsChange = (e) => {
    const tagsArray = e.target.value.split(/,\s*/);
    setCurrentTags(tagsArray);
  };

  const handleUpdate = async () => {
    const id = article._id;
    const data = {
      title: currentTitle,
      summary: currentSummary,
      description: currentDescription,
      tags: currentTags,
    };
    try {
      const response = await axiosPrivate.put(
        `/articles/update-article/${id}`,
        data
      );
      if (response) {
        toast({
          title: "Article Updated.",
          description: "Your article has been updated successfully.",
          status: "success",
          duration: 2000,
        });
        navigate("/landing/knowledgebase");
      }
    } catch (err) {
      toast({
        title: "Error.",
        description: "There was an error updating your article.",
        status: "error",
        duration: 3000,
      });
      console.error("Error updating article:", err);
    }
  };

  const handleDelete = async () => {
    console.log("Deleted");

    const id = article._id;

    try {
      const response = await axiosPrivate.delete(
        `/articles/delete-article/${id}`
      );
      if (response) {
        toast({
          title: "Article Deleted.",
          description: "The article has been Delete successfully.",
          status: "success",
          duration: 2000,
        });
        navigate("/landing/knowledgebase");
      }
    } catch (err) {
      toast({
        title: "Error.",
        description: "There was an error deleting deleting article.",
        status: "error",
        duration: 3000,
      });
      console.error("Error updating article:", err);
    }
  };

  const canView = hasRequiredRole(["Admin", "SuperAdmin", "Manager"]);

  return (
    <Box>
      <Card bg={cardBgColor}>
        <Box
          display="flex"
          justifyContent="space-between"
          p={5}
          alignItems="center"
          gap={"20px"}
        >
          {isEditing ? (
            <>
              <InputGroup>
                <InputLeftAddon>Title</InputLeftAddon>
                <Input
                  value={currentTitle}
                  onChange={(e) => setCurrentTitle(e.target.value)}
                  size="md"
                  borderRadius={"4px"}
                />
              </InputGroup>
              <InputGroup>
                <InputLeftAddon>Tags</InputLeftAddon>
                <Input
                  value={currentTags.join(", ")}
                  onChange={handleTagsChange}
                  size="md"
                  borderRadius={"4px"}
                />
              </InputGroup>
            </>
          ) : (
            <Heading color={headingColor} size={"md"} display={"flex"} gap={2}>
              {currentTitle}
            </Heading>
          )}
          <Box display={"flex"}>
            <Button
              colorScheme="blue"
              onClick={handleEditClick}
              borderRadius={"4px"}
              bg={buttonBgColor}
            >
              {isEditing ? "Save" : "Edit"}
            </Button>
            {isEditing ? (
              <Button
                colorScheme="blue"
                onClick={() => setIsEditing(false)}
                borderRadius={"4px"}
                ml={2}
                bg={buttonBgColor}
              >
                Cancel
              </Button>
            ) : !canView ? null : (
              <Button
                colorScheme="red"
                onClick={handleDelete}
                borderRadius={"4px"}
                bg="red.500"
                ml={2}
              >
                Delete
              </Button>
            )}
          </Box>
        </Box>
        <Box display="flex" px={5} py={2} alignItems="center" gap={"5px"}>
          {currentTags.map((tag, i) => (
            <Tag key={i} colorScheme={"blue"} variant={"solid"}>
              {tag}
            </Tag>
          ))}
        </Box>
        <Divider borderColor={dividerColor} />
        <Flex p={5} justify={"space-between"}>
          <Flex gap={2}>
            <Text color={textColor}>
              <b>Author</b>: {article.author}
            </Text>
            {article.modifiedByUsername ? (
              <Text color={textColor}>
                <b>Last Modified by</b>: {article.modifiedByUsername}
              </Text>
            ) : (
              ""
            )}
          </Flex>
          <Text color={textColor}>Viewed {article.views} times</Text>
        </Flex>
        <Box p={5}>
          {isEditing ? (
            <>
              <Box pb={5}>
                <InputGroup>
                  <InputLeftAddon>Summary</InputLeftAddon>
                  <Input
                    value={currentSummary}
                    onChange={(e) => setCurrentSummary(e.target.value)}
                    size="md"
                    borderRadius={"4px"}
                  />
                </InputGroup>
              </Box>
              <ReactQuill
                theme="snow"
                formats={formats}
                modules={modules}
                value={currentDescription}
                onChange={setCurrentDescription}
              />
            </>
          ) : (
            <Box
              onClick={(e) => {
                if (e.target.tagName === "IMG") {
                  setPhotoIndex(images.indexOf(e.target.src));
                  setIsOpen(true);
                }
              }}
              p={5}
              color={textColor}
              sx={{
                "*": { fontSize: "14px", lineHeight: "1.6" }, // Consistency in text size
                p: { marginBottom: "1rem" }, // Add spacing for paragraphs
                ul: {
                  paddingLeft: "40px", // Standard bullet list padding
                  listStyleType: "disc", // Ensure bullets appear
                  marginBottom: "1rem",
                },
                ol: {
                  paddingLeft: "40px", // Standard numbered list padding
                  listStyleType: "decimal",
                  marginBottom: "1rem",
                },
                li: {
                  marginBottom: "5px", // Adds space between list items
                },
                // Fix spacing for nested lists (sub-bullets)
                "li > ul, li > ol": {
                  paddingLeft: "20px", // Extra indentation for sub-bullets
                  marginTop: "5px", // Space between parent and child list
                },
                "h1, h2, h3, h4, h5, h6": {
                  marginBottom: "10px",
                  fontWeight: "bold",
                },
                "strong, b": { fontWeight: "bold" }, // Ensure bold text appears properly
                "em, i": { fontStyle: "italic" }, // Ensure italic text appears
                br: { display: "block", marginBottom: "10px" }, // Ensures line breaks have proper spacing
                a: { color: "blue", textDecoration: "underline" }, // Ensure links are styled
              }}
            >
              <Box
                as="div"
                dangerouslySetInnerHTML={{ __html: currentDescription }}
              />
            </Box>
          )}
        </Box>
      </Card>
      {isOpen && (
        <Lightbox
          mainSrc={images[photoIndex]}
          nextSrc={images[(photoIndex + 1) % images.length]}
          prevSrc={images[(photoIndex + images.length - 1) % images.length]}
          onCloseRequest={() => setIsOpen(false)}
          onMovePrevRequest={() =>
            setPhotoIndex((photoIndex + images.length - 1) % images.length)
          }
          onMoveNextRequest={() =>
            setPhotoIndex((photoIndex + 1) % images.length)
          }
        />
      )}
    </Box>
  );
}

export default Article;
